import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { timetableService, classService } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const Timetable: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [classes, setClasses] = useState<any[]>([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [timetableEntries, setTimetableEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const timeSlots = [
    '08:00:00', '09:00:00', '10:00:00', '11:00:00', '12:00:00', '13:00:00'
  ];

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchTimetable();
    }
  }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const response = await classService.getAll();
      setClasses(response.data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchTimetable = async () => {
    setLoading(true);
    try {
      const response = await timetableService.getClassTimetable(selectedClass);
      const data = response.data.timetable || [];
      setTimetableEntries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching timetable:', error);
      setTimetableEntries([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title="Timetable Management">
      <div className="space-y-6">
        {/* Class Selection */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
          <div className="flex items-center justify-between">
            <div className="flex-1 max-w-md">
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Select Class
              </label>
              <select
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
              >
                <option value="">-- Select Class --</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} - {cls.section}
                  </option>
                ))}
              </select>
            </div>
            {selectedClass && (
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {showCreateForm ? 'Cancel' : 'Create/Edit Timetable'}
              </button>
            )}
          </div>
        </div>

        {/* Timetable Display */}
        {selectedClass && !showCreateForm && (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
            {loading ? (
              <div className={`p-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading timetable...</div>
            ) : timetableEntries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                        Day / Time
                      </th>
                      {timeSlots.map((slot) => (
                        <th key={slot} className={`px-6 py-3 text-center text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                          {slot.substring(0, 5)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {daysOfWeek.map((day) => (
                      <tr key={day}>
                        <td className={`px-6 py-4 whitespace-nowrap font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {day}
                        </td>
                        {timeSlots.map((timeSlot) => {
                          const entry = timetableEntries.find(
                            (e: any) => e.dayOfWeek === day && e.startTime === timeSlot
                          );
                          
                          return (
                            <td key={timeSlot} className="px-6 py-4 text-center text-sm">
                              {entry ? (
                                <div>
                                  <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {entry.subject?.name || 'N/A'}
                                  </div>
                                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {entry.teacher?.user?.firstName} {entry.teacher?.user?.lastName}
                                  </div>
                                  <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                    {entry.startTime?.substring(0, 5)} - {entry.endTime?.substring(0, 5)}
                                  </div>
                                </div>
                              ) : (
                                <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No timetable found for this class</p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                >
                  Create Timetable
                </button>
              </div>
            )}
          </div>
        )}

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Create/Edit Timetable</h3>
            <div className="text-center py-8">
              <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📅 Timetable builder coming soon!</p>
              <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>This feature will allow you to create and edit class timetables with drag-and-drop functionality.</p>
              <div className="mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Go Back
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Timetable;
